<template>
  <v-row no-gutters class="pr-5">
    <v-col cols="12" class="px-0 pt-0" v-if="isFetched && mapPanelDataFetched">
      <sidebar-row class="pt-3">
        <v-col cols="auto" class="px-0 pt-0">
          <v-row no-gutters class="text-body-2">
            {{ $i18n.t('sidebar.locks.valves') }}
          </v-row>
        </v-col>
      </sidebar-row>
      <sidebar-row :border-bottom="!isAnalysisDone || (resultLayers.length < 1 && !deaerationDone)">
        <v-col cols="auto" class="px-0 pt-0">
          <v-row no-gutters class="align-center pb-3" v-for="(state, idx) in valvesStateStyles" :key="idx">
            <v-col cols="auto" class="pr-3">
              <layer-style-preview-icon is-icon layer-type="point" :layer-style="state" :custom-size="28" />
            </v-col>
            <v-col cols="auto" class="text-body-2 pt-1">
              {{ $i18n.t(`sidebar.locks.${state.text}`) }}
            </v-col>
          </v-row>
        </v-col>
      </sidebar-row>
      <template v-if="isAnalysisDone && resultLayers.length > 0">
        <sidebar-row class="pt-3">
          <v-col cols="auto" class="px-0 pt-0">
            <v-row no-gutters class="text-body-2">
              {{ $i18n.t('sidebar.locks.facilitiesWithoutSupply') }}
            </v-row>
          </v-col>
        </sidebar-row>
        <sidebar-row :border-bottom="!deaerationDone">
          <v-col cols="auto" class="px-0 pt-0">
            <v-row no-gutters class="align-center pb-2" v-for="(layer, idx) in resultLayers" :key="idx">
              <v-col cols="auto" class="pr-3">
                <layer-style-preview-icon
                  is-icon
                  :layer-type="getLayerType(layer)"
                  :layer-style="layer.style"
                  :custom-size="28"
                />
              </v-col>
              <v-col cols="auto" class="text-body-2">
                {{ layer.name }}
              </v-col>
            </v-row>
          </v-col>
        </sidebar-row>
      </template>
      <template v-if="deaerationDone">
        <sidebar-row class="pt-3">
          <v-col cols="auto" class="px-0 pt-0">
            <v-row no-gutters class="text-body-2">
              {{ $i18n.t('sidebar.locks.hydrantRanking') }}
            </v-row>
          </v-col>
        </sidebar-row>
        <sidebar-row border-bottom>
          <v-col cols="auto" class="px-0 pt-0">
            <legend-layers-list-styles
              :default-style="deaerationStyle"
              :styles="deaerationStyleRanges"
              layer-type="point"
              :has-permission="true"
              default-style-label-path="default.noData"
              :max-styles="6"
            />
          </v-col>
        </sidebar-row>
      </template>
      <v-row no-gutters class="pt-3">
        <v-col cols="12">
          <stepper
            ref="locksAnalysisStepper"
            editable
            :steps="steps"
            :stepper-props="stepperProps"
            :stepper-classes="stepperClasses"
            :stepper-header-classes="stepperHeaderClasses"
            :stepper-buttons-classes="stepperButtonsClasses"
            @stepChange="stepChange"
          >
            <template #1>
              <v-row no-gutters class="pb-5">
                <template v-for="input of inputs">
                  <sidebar-form-row :key="input.name" v-bind="input" v-model="payload[input.name]">
                    <template slot="locks-analysis-geometry">
                      <sidebar-geometry-input
                        ref="locks-analysis-geometry"
                        v-model="payload[input.name]"
                        v-bind="input"
                        :is-valid.sync="isGeometryValid"
                      />
                    </template>
                  </sidebar-form-row>
                </template>
              </v-row>
            </template>
            <template #2>
              <v-row no-gutters class="pb-5">
                <v-spacer />
                <v-col cols="11">
                  <base-button-toggleable
                    translation-path="button.exclude"
                    :is-active.sync="isExclusionActive"
                    :subheader-translation-path="[
                      'sidebar.locks.indicateValvesOnMap',
                      'sidebar.locks.excludesValvesAsOpen',
                    ]"
                    :has-badge="excludesValvesIds && excludesValvesIds.length > 0"
                    :badge-value="excludesValvesIds.length"
                    @change="toggleExclusion"
                  />
                </v-col>
              </v-row>
              <v-row no-gutters v-if="excludesValvesIds.length > 0" class="pb-3">
                <v-spacer />
                <v-col cols="11">
                  <v-list class="py-0 elements-list" dense>
                    <v-list-item v-for="(valve, idx) in excludesValves" :key="idx" class="valves-element px-2">
                      <v-list-item-content class="list-element-content text-body-2">
                        <v-tooltip bottom color="font">
                          <template #activator="{ on }">
                            <span v-on="on">
                              {{ getValveLabel(valve) }}
                            </span>
                          </template>
                          {{ getValveLabel(valve) }}
                        </v-tooltip>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-row no-gutters align="center">
                          <v-col cols="auto" class="pa-0">
                            <v-icon size="20" @click="zoomToValve(valve.id)">
                              mdi-magnify
                            </v-icon>
                          </v-col>
                          <v-col cols="auto" class="pa-0">
                            <dots-menu :items="excludedValvesMenuItems" @delete="deleteValve(valve.id)" />
                          </v-col>
                        </v-row>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-col>
              </v-row>
            </template>
            <template #3>
              <v-row no-gutters class="pb-2" :class="{ 'no-print': isCardInfoPrint }">
                <v-col cols="1"></v-col>
                <v-col cols="auto" v-if="isAnalysisDone">
                  <base-button-subheader
                    translation-path="sidebar.locks.reset"
                    @click="resetAnalysis"
                    :disabled="isAnalysisLoading"
                    cancel
                  />
                </v-col>
                <v-col cols="auto">
                  <base-button-subheader
                    :translation-path="
                      isAnalysisDone ? 'sidebar.locks.turnOnAnalysisAgain' : 'sidebar.locks.turnOnAnalysis'
                    "
                    @click="turnOnAnalysis"
                    :loading="isAnalysisLoading"
                  />
                </v-col>
                <v-spacer />
                <v-col cols="auto" v-if="isAnalysisDone">
                  <base-rounded-button
                    icon="mdi-printer"
                    size="x-small"
                    :tooltip="$i18n.t('button.printAnalysisResult')"
                    @click="$root.$emit('printCardInfo')"
                  />
                </v-col>
              </v-row>
              <sidebar-row class="pb-5" :class="{ 'no-print': isCardInfoPrint }" v-if="isAnalysisDone">
                <link-underlined
                  @click="openModelingDialog"
                  :disabled="!isEpanetCutoffModelingEnabled"
                  translation-path="sidebar.locks.runModel"
                />
              </sidebar-row>
              <template v-if="isAnalysisDone && !isAnalysisLoading">
                <sidebar-row class="pt-3">
                  <v-col cols="auto" class="px-0 pt-0">
                    <v-row no-gutters class="text-body-2 font-weight-medium">
                      <v-badge
                        overlap
                        :content="cutoffResult.water_valves.length"
                        :value="cutoffResult.water_valves.length > 0"
                        color="error"
                        :offset-x="-3"
                      >
                        {{ $i18n.t('sidebar.locks.valvesToCloseList') }}
                      </v-badge>
                    </v-row>
                  </v-col>
                </sidebar-row>
                <v-row no-gutters v-if="cutoffResult.water_valves.length > 0" :class="{ 'pr-5': isCardInfoPrint }">
                  <v-spacer />
                  <v-col cols="11">
                    <v-list class="py-0 elements-list" dense>
                      <v-list-item
                        v-for="(valve, idx) in cutoffResult.water_valves"
                        :key="idx"
                        class="valves-element hover-list-element px-2"
                        :style="`break-inside: ${isCardInfoPrint ? 'avoid' : 'auto'}`"
                      >
                        <v-list-item-content class="list-element-content text-body-2">
                          <v-tooltip bottom color="font">
                            <template #activator="{ on }">
                              <span v-on="on">
                                {{ getValveLabel(valvesToClose.find(valveToClose => valveToClose.id === valve)) }}
                              </span>
                            </template>
                            {{ getValveLabel(valvesToClose.find(valveToClose => valveToClose.id === valve)) }}
                          </v-tooltip>
                        </v-list-item-content>
                        <v-list-item-action :class="{ 'no-print': isCardInfoPrint }">
                          <v-row no-gutters align="center">
                            <v-col cols="auto" class="pa-0">
                              <v-icon size="20" @click="zoomToValve(valve)">
                                mdi-magnify
                              </v-icon>
                            </v-col>
                            <v-col cols="auto" class="pa-0" v-if="isDeaerationEnabled">
                              <v-tooltip bottom color="font">
                                <template #activator="{ on }">
                                  <v-icon
                                    v-on="on"
                                    size="20"
                                    :color="deaerationActiveValveId === valve ? 'primary' : ''"
                                    :class="{ 'active-deaeration-play-button': deaerationActiveValveId === valve }"
                                    @click="runDeaeration(valve)"
                                  >
                                    mdi-play
                                  </v-icon>
                                </template>
                                {{
                                  deaerationActiveValveId === valve
                                    ? $i18n.t('sidebar.locks.turnOnDeaerationAgain')
                                    : $i18n.t('sidebar.locks.turnOnDeaeration')
                                }}
                              </v-tooltip>
                            </v-col>
                          </v-row>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list>
                  </v-col>
                </v-row>
                <sidebar-row>
                  <sidebar-summary :items="summaryItems" class="pt-6">
                    <template v-for="item of summaryItems" #[item.name]="">
                      {{ cutoffResult.water_watermeters.length }}
                    </template>
                  </sidebar-summary>
                </sidebar-row>
                <sidebar-row
                  class="pb-3"
                  :class="{ 'no-print': isCardInfoPrint }"
                  v-if="$route.name !== 'locksAnalysisLayer'"
                >
                  <link-underlined @click="openWatermeters" translation-path="sidebar.locks.selectWatermeters" />
                </sidebar-row>
              </template>
            </template>
            <template #4 v-if="isDeaerationEnabled">
              <sidebar-row class="pt-3 pb-3" v-if="deaerationDone">
                <v-row no-gutters align="center" class="pb-5">
                  <v-col cols="auto" class="px-0 pt-0 text-body-2 font-weight-medium">
                    <v-row no-gutters>
                      {{ $i18n.t('sidebar.locks.hydrantRanking') }}
                    </v-row>
                    <v-row no-gutters>
                      <italic-subheader
                        translationPath="sidebar.locks.deaerationValveID"
                        :params="{ valve: getValveLabel(deaerationActiveValve) }"
                      />
                    </v-row>
                  </v-col>
                  <v-spacer />
                  <v-col cols="auto">
                    <base-rounded-button
                      icon="mdi-printer"
                      size="x-small"
                      :tooltip="$i18n.t('button.printAnalysisResult')"
                      @click="$root.$emit('printCardInfo')"
                    />
                  </v-col>
                </v-row>
                <v-row no-gutters align="center">
                  <v-col cols="12">
                    <v-list class="py-0 elements-list" dense>
                      <v-list-item
                        v-for="(hydrant, idx) in activeDeaerationResultArray"
                        :key="idx"
                        class="hydrant-element px-2"
                        :class="{ 'hydrant-element-green': activeDeaerationResultBestHydrants.includes(hydrant.id) }"
                      >
                        <v-list-item-content class="list-element-content">
                          <v-col cols="12">
                            <v-row no-gutters class="text-body-2">
                              <v-tooltip bottom color="font">
                                <template #activator="{ on }">
                                  <span v-on="on">
                                    {{ `ID: ${hydrant.id}` }}
                                  </span>
                                </template>
                                {{ `ID: ${hydrant.id}` }}
                              </v-tooltip>
                            </v-row>
                            <v-row no-gutters>
                              <italic-subheader
                                translationPath="sidebar.locks.deaerationValue"
                                :params="{
                                  value:
                                    hydrant.value || hydrant.value === 0 ? hydrant.value : $i18n.t('default.noData'),
                                }"
                              />
                            </v-row>
                          </v-col>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-row no-gutters align="center">
                            <v-col cols="auto" class="pa-0">
                              <v-icon size="20" @click="zoomToDeaerationHydrant(hydrant.id)">
                                mdi-magnify
                              </v-icon>
                            </v-col>
                          </v-row>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list>
                  </v-col>
                </v-row>
              </sidebar-row>
            </template>
          </stepper>
        </v-col>
      </v-row>
      <main-input-dialog
        :is-visible.sync="isModelingDialogVisible"
        :loading="isModelingLoading"
        :title-translation="$i18n.t('dialog.title.lockAnalysisModeling')"
        :inputs="modelingModalInputs"
        dialog-width="35%"
        agree-text="dialog.agreeModel"
        @agree="runModel"
      >
        <template #upperForm>
          <v-alert
            type="warning"
            icon="mdi-alert-outline"
            dense
            text
            class="text-left mb-10 text-caption"
            style="width: 100%"
          >
            {{ $i18n.t('sidebar.locks.alertMessage') }}
          </v-alert>
        </template>
        <template #[`modelingProfile.input`]="data">
          <v-col cols="6">
            <data-input v-model="selectedEpanetProfile" v-bind="data" />
          </v-col>
        </template>
      </main-input-dialog>
    </v-col>
    <template v-else>
      <main-loader class="pt-10" />
    </template>
  </v-row>
</template>
<script>
import { call, get, sync } from 'vuex-pathify';
import locks from '@/mixins/locks';
import DataInput from '@/components/DataInput';
import ItalicSubheader from '@/components/ItalicSubheader';
import LegendLayersListStyles from '@/components/LegendLayersListStyles';

import profiles from '@/mixins/profiles';
import getBbox from '@/mixins/map/getBbox';
import stylePreviewUtilities from '@/mixins/stylePreviewUtilities';

export default {
  name: 'LocksAnalysisTool',
  mixins: [locks, profiles, getBbox, stylePreviewUtilities],
  components: {
    DataInput,
    ItalicSubheader,
    LegendLayersListStyles,
    SidebarRow: () => import('@/components/SidebarRow'),
    Stepper: () => import('@/components/Stepper'),
    LayerStylePreviewIcon: () => import('@/components/LayerStylePreviewIcon'),
    MainLoader: () => import('@/components/MainLoader'),
    SidebarGeometryInput: () => import('@/components/SidebarGeometryInput'),
    SidebarFormRow: () => import('@/components/SidebarFormRow'),
    DotsMenu: () => import('@/components/DotsMenu'),
    SidebarSummary: () => import('@/components/SidebarSummary'),
    LinkUnderlined: () => import('@/components/LinkUnderlined'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  computed: {
    isEpanetCutoffModelingEnabled: get('admin/settingsValues@epanet_cutoff_enabled'),
    isDeaerationEnabled: get('admin/settingsValues@failures_cutoff_deaeration_enabled'),
    layers: get('layers/layers'),
    layersArray() {
      return this.$_objectToArray(this.layers);
    },
    dataSources: get('layers/metadata'),
    hydrantsLayer() {
      return this.layersArray.find(layer => layer.role === 'water_hydrants');
    },
    waterPipesLayer() {
      return this.layersArray.find(layer => layer.role === 'water_pipes');
    },
    activeTool: get('tools/activeTool'),
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    cutoffProject: sync('locks/cutoffProject'),
    cutoffResult: sync('locks/cutoffResult'),
    cutoffValvesLabel: get('admin/settingsValues@cutoff_valves_label'),
    excludesValves: sync('locks/excludesValves'),
    isSmsModuleEnabled: get('admin/additionalModules@SMS@enabled'),
    isSmsSettingEnabled: get('admin/settingsValues@sms_module_enabled'),
    locksAnalysisLayersIds: get('admin/modulesMapping@failures.cutoff_analysis_layers_ids'),
    resultLayers: sync('locks/resultLayers'),
    valvesToClose: sync('locks/valvesToClose'),
    isCardInfoPrint: get('tools/toolStatus@isCardInfoPrintActive'),
    epanetProfiles: get('epanet/epanetProfiles'),
    cutoffValvesLabelVars() {
      const vars = [];
      const rxp = /{([^}]+)}/g;
      let curMatch;

      while ((curMatch = rxp.exec(this.cutoffValvesLabel))) {
        vars.push(curMatch[1]);
      }
      return vars;
    },
    excludesValvesIds() {
      return this.excludesValves.map(valve => valve.id);
    },
    valvesLayerId() {
      return this.locksAnalysisLayersIds['water_valves'];
    },
    watermetersLayerId() {
      return this.locksAnalysisLayersIds['water_watermeters'];
    },
    modelingModalInputs() {
      return [
        {
          name: 'modelingProfile',
          translationPath: 'sidebar.locks.modelingProfile',
          subtitle: 'sidebar.locks.modelingProfileSettings',
          dataType: {
            name: 'select',
          },
          disabled: !this.areProfilesLoaded,
          loading: !this.areProfilesLoaded,
          items: this.epanetProfiles,
          itemText: 'name',
          returnObject: true,
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
      ];
    },
    inputs() {
      return [
        {
          name: 'locks-analysis-geometry',
          icon: 'mdi-map-marker',
          iconSize: '22px',
          hasNameColumn: false,
          oneLineInput: false,
          cols: '12',
          drawGeometryText: 'sidebar.locks.indicateFailurePoint',
          hint: this.$i18n.t('sidebar.locks.clickOnMapAnalysisGeometry'),
          hintWithValue: this.$i18n.t('sidebar.clickDotsToDeleteGeometry'),
          geometryType: 'Point',
          hasMarkerSymbolization: true,
        },
      ];
    },
    steps() {
      return [
        {
          step: 1,
          translationPath: 'sidebar.locks.failurePlace',
          backDisabled: this.isAnalysisLoading,
          nextDisabled: !this.isGeometryValid || this.isAnalysisLoading,
        },
        {
          step: 2,
          translationPath: 'sidebar.locks.valvesExclusion',
          subtitlePath: 'default.optional',
          backDisabled: this.isAnalysisLoading,
          nextDisabled: this.isAnalysisLoading,
        },
        {
          step: 3,
          translationPath: 'sidebar.locks.analysis',
          backDisabled: this.isAnalysisLoading,
          nextDisabled: this.isAnalysisLoading || !this.deaerationDone,
        },
        ...(this.isDeaerationEnabled
          ? [
              {
                step: 4,
                translationPath: 'sidebar.locks.deaeration',
                backDisabled: this.isAnalysisLoading,
                nextDisabled: this.isAnalysisLoading,
                disabled: !this.deaerationDone,
              },
            ]
          : []),
      ];
    },
    summaryItems() {
      return [
        {
          name: 'numerOfWatermetersInFailure',
          translationPath: 'sidebar.locks.numerOfWatermetersInFailure',
          color: 'rgba(0,145,234)',
          titleCols: 'auto',
          titleClasses: ['pr-4'],
          rowClasses: ['pt-2'],
        },
      ];
    },
    deaerationDone() {
      return this.deaerationActiveValveId || this.deaerationActiveValveId === 0;
    },
    activeDeaerationResult() {
      return this.cutoffResult?.deaeration?.[this.deaerationActiveValveId] || {};
    },
    activeDeaerationResultArray() {
      return Object.keys(this.activeDeaerationResult)
        .map(key => {
          return { value: this.activeDeaerationResult[key], id: parseInt(key) };
        })
        .sort((a, b) => b.value - a.value);
    },
    activeDeaerationResultUniquesValues() {
      return Array.from(new Set(this.activeDeaerationResultArray.map(hydrant => hydrant.value))).filter(
        hydrant => !!hydrant || hydrant === 0
      );
    },
    activeDeaerationResultBestHydrants() {
      const filteredHydrants = this.activeDeaerationResultArray.filter(hydrant => hydrant.value || hydrant.value === 0);
      const min = filteredHydrants?.length ? Math.max(...filteredHydrants.map(hydrant => hydrant.value)) : null;
      if (!min && min !== 0) return [];
      return filteredHydrants.filter(hydrant => hydrant.value === min).map(hydrant => hydrant.id);
    },
    deaerationActiveValve() {
      return this.valvesToClose.find(valveToClose => valveToClose.id === this.deaerationActiveValveId);
    },
    deaerationStyleRanges() {
      return this.deaerationStyle.ranges.values.map(value => {
        return {
          ...value,
          key: value.label || value.label === '' ? value.label : value.value,
          color: this.getStyleIconColor(value),
          icon: this.getStyleExternalGraphicIcon(value.externalGraphic),
        };
      });
    },
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
  },
  data: () => ({
    excludedValvesMenuItems: [
      {
        name: 'delete',
      },
    ],
    isAnalysisDone: false,
    isAnalysisLoading: false,
    isExclusionActive: false,
    isFetched: false,
    isGeometryValid: false,
    payload: {},
    isModelingDialogVisible: false,
    isModelingLoading: false,
    areProfilesLoaded: false,
    selectedEpanetProfile: null,
    stepperClasses: ['elevation-0'],
    stepperButtonsClasses: ['pl-8'],
    stepperHeaderClasses: ['elevation-0', 'pl-5'],
    stepperProps: {
      altLabels: true,
    },
    deaerationActiveValveId: null,
    deaerationStyle: {},
  }),
  methods: {
    getCutoffResult: call('locks/getCutoffResult'),
    getEpanetProfiles: call('epanet/getEpanetProfiles'),
    runEpanetModel: call('epanet/runModel'),
    getLayerFeatures: call('layers/getLayerFeatures'),
    cutoffResultFetched() {
      this.isAnalysisDone = true;
      this.isAnalysisLoading = false;
    },
    deleteValve(id) {
      this.$root.$emit('deleteExcludedLocksFeature', id);
    },
    async getPayload() {
      const bbox = await this.getFeaturesBbox({
        layerId: this.waterPipesLayer?.id,
        featuresIds: this.cutoffResult['water_pipes'],
      });
      const modelingProfile = this.getWaterModelingProfilePayload(this.selectedEpanetProfile);
      const payload = {
        ...modelingProfile,
        force_attributes: {
          water_valves: {
            model_fixed_status: {
              value: 'ZAMKNIĘTY',
              features: this.valvesToClose.map(valve => valve.id),
            },
          },
        },
        bbox,
      };
      return payload;
    },
    async openModelingDialog() {
      this.isModelingDialogVisible = true;
      if (!this.areProfilesLoaded) {
        await this.getEpanetProfiles();
        this.areProfilesLoaded = true;
      }
    },
    async runModel() {
      this.isModelingLoading = true;
      try {
        const payload = await this.getPayload();
        const r = await this.runEpanetModel(payload);
        this.$router.push({
          name: 'modelingResultsScenario',
          params: {
            netType: 'water',
            scenarioId: r.scenario_id,
          },
        });
        localStorage.setItem(`scenarioId-water`, r.scenario_id);
      } finally {
        this.isModelingLoading = false;
      }
    },
    getValveLabel(valve) {
      let label = JSON.parse(JSON.stringify(this.cutoffValvesLabel));
      for (const labelVar of this.cutoffValvesLabelVars) {
        label = label.replace(new RegExp('{' + labelVar + '}', 'gi'), valve[labelVar] || '');
      }
      return label;
    },
    locksAnalysisValvesFetched() {
      this.isFetched = true;
    },
    openWatermeters() {
      if (!this.projectLayers.map(layer => layer.id).includes(this.watermetersLayerId)) {
        const layer = JSON.parse(JSON.stringify(this.layers[this.watermetersLayerId]));
        if (layer) {
          layer.labels_visible = layer.labels_visible || false;
          layer.style = layer.style_web;
          delete layer.style_web;
          this.$store.set('layers/ADD_PROJECT_LAYERS!', [
            { layer_id: layer.id, visible: true, opacity: 1, has_permission: true, ...layer },
          ]);
          this.$root.$emit('pushProjectLayers', [
            { layer_id: layer.id, visible: true, opacity: 1, has_permission: true, ...layer },
          ]);
          this.$store.set('snackbar/PUSH_MESSAGE!', {
            message: this.$i18n.t('snackbar.layerAddedToProject', { name: layer.name }),
          });
        }
      }
      this.$router.push({ name: 'locksAnalysisLayer', params: { lid: this.watermetersLayerId } });
    },
    resetAnalysis() {
      this.deaerationActiveValveId = null;
      this.$root.$emit('toggleDeaerationHydrants');
      if (this.$route.name === 'locksAnalysisLayer') {
        this.$router.push({ name: 'locksAnalysis' });
      }
      this.isAnalysisDone = false;
      this.cutoffResult = undefined;
      this.$refs['locks-analysis-geometry'][0].deleteGeometry();
      this.$root.$emit('deleteExcludedLocksFeatures');
      this.$root.$emit('toggleValvesToCloseLayer');
      this.$root.$emit('toggleLocksResultLayers', false);
      this.$refs['locksAnalysisStepper'].reset();
    },
    selectFeatures(layerId, features) {
      this.$store.set('map/ADD_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [layerId]: features,
        },
      });
    },
    stepChange(value) {
      if (value.oV == '2') {
        this.isExclusionActive = false;
      }
    },
    async turnOnAnalysis() {
      this.deaerationActiveValveId = null;
      this.$root.$emit('toggleDeaerationHydrants');
      if (this.$route.name === 'locksAnalysisLayer') {
        this.$router.push({ name: 'locksAnalysis' });
      }
      this.isAnalysisLoading = true;
      try {
        const geom = this.payload['locks-analysis-geometry'];
        const coords = geom.coordinates;
        const payload = {
          x: coords[0],
          y: coords[1],
          srid: geom.crs.properties.name.split(':')[1],
          excluded_valves: this.excludesValvesIds.join(),
        };
        await this.getCutoffResult(payload);
        const layers = [];
        const resultKeys = Object.keys(this.cutoffResult);
        for (const resultKey of resultKeys) {
          const resultKeyId = this.locksAnalysisLayersIds[resultKey];
          if (resultKeyId || resultKeyId == '0') {
            const layer = JSON.parse(
              JSON.stringify(this.cutoffProject.layers.find(layer => layer.id == resultKeyId) || '')
            );
            if (layer) {
              layers.push({ ...layer, locksKey: resultKey });
            }
          }
        }
        this.resultLayers = layers;
        this.$root.$emit('toggleValvesToCloseLayer', true);
        this.$root.$emit('toggleLocksResultLayers', true);
      } catch {
        this.isAnalysisLoading = false;
      }
    },
    async runDeaeration(valve) {
      try {
        this.deaerationActiveValveId = valve;
        this.deaerationStyle = this.getHydrantsDeaerationStyle(
          this.getHydrantsDeaerationRanges(this.activeDeaerationResultUniquesValues)
        );
        const hydrantsDataSource = this.dataSources[this.hydrantsLayer?.data_source_name];
        const idAttributeName = hydrantsDataSource?.attributes_schema?.id_name;
        const idAttribute = hydrantsDataSource.attributes_use_datasource_qualified_names
          ? idAttributeName
          : `${hydrantsDataSource.name}.${idAttributeName}`;
        const payload = {
          layer_id: this.hydrantsLayer?.id,
          features_filter: {
            $IN: {
              [idAttribute]: Object.keys(this.activeDeaerationResult),
            },
          },
          params: {
            with_features: true,
            with_collection_bbox: false,
            with_count: false,
            with_geometry: true,
          },
        };
        const r = await this.getLayerFeatures(payload);
        let features = r.data.data.features;
        features.features = features.features.map(feature => {
          return {
            ...feature,
            properties: { deaeration: this.activeDeaerationResult[feature.id] || this.$i18n.t('default.noData') },
          };
        });
        this.$root.$emit('toggleDeaerationHydrants', {
          features,
          style: JSON.parse(JSON.stringify(this.deaerationStyle)),
        });
        this.$refs['locksAnalysisStepper']?.next();
      } catch {
        //
      }
    },
    zoomToDeaerationHydrant(id) {
      this.$root.$emit('zoomToFeature', 'locks-analysis-deaeration', '', id);
    },
    zoomToValve(id) {
      this.$root.$emit('zoomToFeature', this.valvesLayerId, 'locksLayers', id);
    },
    toggleExclusion(value) {
      if (value) {
        this.$root.$emit('identification-action', true, { specialType: 'excludeObject' });
      } else {
        this.$root.$emit('identification-action', false);
      }
    },
  },
  watch: {
    activeTool(nV, oV) {
      if (nV !== oV && oV === 'identification-excludeObject') {
        this.isExclusionActive = false;
      }
    },
    mapPanelDataFetched(nV) {
      if (nV) {
        this.$root.$emit('toggleValvesLayer', true);
      }
    },
  },
  mounted() {
    this.$root.$off('locksAnalysisValvesFetched');
    this.$root.$on('locksAnalysisValvesFetched', this.locksAnalysisValvesFetched);
    this.$root.$on('tableDataRowsFetched', () => {
      this.selectFeatures(this.watermetersLayerId, this.cutoffResult.water_watermeters);
    });
    this.$root.$off('excludedObjects');
    this.$root.$on('excludedObjects', identifiedFeatures => {
      for (const identifiedLayer of identifiedFeatures) {
        if (identifiedLayer.id == this.valvesLayerId) {
          for (const identifiedFeature of identifiedLayer.features) {
            const featureId = identifiedFeature.id;
            if (!this.excludesValvesIds.includes(featureId)) {
              this.$root.$emit('addExcludedLocksFeature', featureId);
            }
          }
        }
      }
    });
    this.$root.$off('cutoffResultFetched');
    this.$root.$on('cutoffResultFetched', this.cutoffResultFetched);
    if (this.mapPanelDataFetched) {
      this.$root.$emit('toggleValvesLayer', true);
    }
  },
  beforeDestroy() {
    this.$root.$off('tableDataRowsFetched');
    this.$root.$emit('identification-action', false);
    this.excludesValves = [];
    this.$root.$emit('deleteLocksLayers');
    this.$root.$emit('toggleValvesToCloseLayer', false);
    this.$root.$emit('toggleLocksResultLayers', false);
    this.$root.$emit('toggleDeaerationHydrants');
  },
};
</script>
<style lang="scss" scoped>
.elements-list {
  border-radius: 4px;
  @include border(top);
  @include border(right);
  @include border(left);
}
.valves-element {
  @include border(bottom);
  text-align: left;
  min-height: 36px;
  max-height: 36px;
}
.list-element-content {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
}
.hydrant-element {
  @include border(bottom);
  text-align: left;
  min-height: 44px;
  max-height: 44px;
}
.hydrant-element:not(.hydrant-element-green):hover {
  background-color: rgba(188, 188, 188, 0.2);
}
.hydrant-element-green {
  @include border(all, 1px, rgba(map-get($colors, 'success'), 0.5));
  background-color: rgba(map-get($colors, 'success'), 0.1);
}
.hydrant-element-green:hover {
  background-color: rgba(map-get($colors, 'success'), 0.2);
}
.active-deaeration-play-button {
  background-color: map-get($colors, 'primarybackground');
  border-radius: 50%;
}
</style>
